import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Building2,
  Search,
  Filter,
  Power,
  PowerOff,
  RefreshCw,
  ChevronDown,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  Shield,
  X,
  Loader2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Activity,
  TrendingUp,
} from 'lucide-react'
import Loader from '../../../components/misc/Loader'
import providersService from '../../../api/ProvidersService'

const ProviderManagement = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [providers, setProviders] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [modalData, setModalData] = useState({})
  const [modalAction, setModalAction] = useState('')
  const [actionLoading, setActionLoading] = useState(false)


  // Fetch providers
  useEffect(() => {
    const fetchProviders = async () => {
      setLoading(true)
      try {
        const response = await providersService.getProviders()
        if (response.status && response.data) {
          // Handle different response structures
          let providersData = []
          if (response.data?.status_code === 200) {
            providersData = response.data.data || []
          } else if (Array.isArray(response.data)) {
            providersData = response.data
          } else if (response.data?.data) {
            providersData = Array.isArray(response.data.data) 
              ? response.data.data 
              : Object.values(response.data.data)
          }
          setProviders(providersData)
        } else {
          toast.error(response.message || 'An error occurred while fetching providers')
        }
      } catch (error) {
        toast.error(error.message || 'An error occurred while fetching providers')
      } finally {
        setLoading(false)
      }
    }
    fetchProviders()
  }, [])

  // Filter and search providers
  const filteredProviders = useMemo(() => {
    return providers
      .filter((provider) => {
        const matchesSearch =
          provider.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          provider.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          provider.phone_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          provider.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          provider.region?.toLowerCase().includes(searchTerm.toLowerCase())

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && provider.status === 1) ||
          (statusFilter === 'inactive' && provider.status === 0)

        return matchesSearch && matchesStatus
      })
      .sort((a, b) => {
        let aValue, bValue

        switch (sortBy) {
          case 'name':
            aValue = (a.company_name || '').toLowerCase()
            bValue = (b.company_name || '').toLowerCase()
            break
          case 'email':
            aValue = (a.email || '').toLowerCase()
            bValue = (b.email || '').toLowerCase()
            break
          case 'location':
            aValue = (a.location || '').toLowerCase()
            bValue = (b.location || '').toLowerCase()
            break
          case 'status':
            aValue = a.status === 1 ? 1 : 0
            bValue = b.status === 1 ? 1 : 0
            break
          default:
            aValue = a[sortBy] || ''
            bValue = b[sortBy] || ''
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1
        } else {
          return aValue < bValue ? 1 : -1
        }
      })
  }, [providers, searchTerm, statusFilter, sortBy, sortOrder])

  // Pagination
  const totalPages = Math.ceil(filteredProviders.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedProviders = filteredProviders.slice(startIndex, endIndex)

  // Calculate stats
  const stats = useMemo(() => {
    const totalProviders = providers.length
    const activeProviders = providers.filter((p) => p.status === 1).length
    const inactiveProviders = providers.filter((p) => p.status === 0).length
    const verifiedProviders = providers.filter(
      (p) => p.email_verified_at || p.phone_number_verified_at
    ).length

    return {
      total: totalProviders,
      active: activeProviders,
      inactive: inactiveProviders,
      verified: verifiedProviders,
      totalPolicies: 0, // Will be calculated on the policies page
    }
  }, [providers])

  const getStatusBadge = (provider) => {
    if (provider.status === 1) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-3 h-3 mr-1.5" />
          Active
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
          <Clock className="w-3 h-3 mr-1.5" />
          Inactive
        </span>
      )
    }
  }

  const getVerificationBadge = (provider) => {
    if (provider.email_verified_at || provider.phone_number_verified_at) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          <Shield className="w-3 h-3 mr-1" />
          Verified
        </span>
      )
    }
    return null
  }

  const handleRefresh = async () => {
    setLoading(true)
    try {
      const response = await providersService.getProviders()
      if (response.status && response.data) {
        let providersData = []
        if (response.data?.status_code === 200) {
          providersData = response.data.data || []
        } else if (Array.isArray(response.data)) {
          providersData = response.data
        } else if (response.data?.data) {
          providersData = Array.isArray(response.data.data) 
            ? response.data.data 
            : Object.values(response.data.data)
        }
        setProviders(providersData)
        toast.success('Providers data refreshed successfully')
      } else {
        toast.error(response.message || 'Failed to refresh providers')
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while refreshing providers')
    } finally {
      setLoading(false)
    }
  }

  // Action handlers
  const handleStatusChange = (provider, action) => {
    setModalData(provider)
    setModalAction(action)
    setShowConfirmModal(true)
  }

  const closeModal = () => {
    setShowConfirmModal(false)
    setModalData({})
    setModalAction('')
    setActionLoading(false)
  }

  // Execute modal action
  const executeAction = async () => {
    setActionLoading(true)
    try {
      // Use the action directly (activate or deactivate)
      const response = await providersService.updateProviderStatus(modalData.slug, modalAction)

      if (response.status) {
        toast.success(response.message || 'Provider status updated successfully')
        await handleRefresh()
        closeModal()
      } else {
        toast.error(response.message || 'Action failed')
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while processing the action')
    } finally {
      setActionLoading(false)
    }
  }

  // View policies handler - Navigate to Insurance Policy Management with provider filter
  const handleViewPolicies = (provider) => {
    navigate(`/admin/insurance-policies?provider=${provider.slug}`, {
      state: { from: location.pathname }
    })
  }


  if (loading) {
    return <Loader fullScreen={true} text="Loading providers..." />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Mobile Container */}
      <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 flex items-center">
              <Building2 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mr-2 sm:mr-3 text-emerald-600 flex-shrink-0" />
              <span className="truncate">Provider Management</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage and monitor all insurance providers
            </p>
          </div>

          {/* Header Actions */}
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="bg-white text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 flex items-center justify-center min-h-[44px] w-full sm:w-auto shadow-sm border border-gray-200 hover:shadow-md"
            >
              <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="hidden xs:inline">Refresh</span>
              <span className="xs:hidden">Reload</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg border border-blue-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-blue-700 truncate">
                  Total Providers
                </p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-900 mt-1">
                  {stats.total}
                </p>
                <div className="flex items-center mt-2">
                  <Activity className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 mr-1 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-blue-700 font-medium">
                    Registered
                  </span>
                </div>
              </div>
              <div className="p-3 bg-blue-200 rounded-xl flex-shrink-0 ml-3 shadow-inner">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-blue-700" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg border border-green-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-green-700 truncate">
                  Active Providers
                </p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-700 mt-1">
                  {stats.active}
                </p>
                <p className="text-xs sm:text-sm text-green-600 mt-2 font-medium">
                  {stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0}% of total
                </p>
              </div>
              <div className="p-3 bg-green-200 rounded-xl flex-shrink-0 ml-3 shadow-inner">
                <Power className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-green-700" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 sm:p-5 lg:p-6 shadow-lg border border-purple-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-purple-700 truncate">
                  Verified Providers
                </p>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-purple-700 mt-1">
                  {stats.verified}
                </p>
                <p className="text-xs sm:text-sm text-purple-600 mt-2 font-medium">
                  {stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0}% verified
                </p>
              </div>
              <div className="p-3 bg-purple-200 rounded-xl flex-shrink-0 ml-3 shadow-inner">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-purple-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200">
          {/* Mobile Filter Toggle */}
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <Filter className="w-4 h-4 mr-2 text-emerald-600" />
              Filters
            </h3>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors touch-manipulation"
            >
              <span>Filters</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${
                  showMobileFilters ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>

          {/* Desktop Filter Header */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Filter className="w-5 h-5 mr-2 text-emerald-600" />
              Filters & Search
            </h3>
          </div>

          {/* Filter Controls */}
          <div
            className={`space-y-4 ${
              showMobileFilters ? 'block' : 'hidden lg:block'
            }`}
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search providers by name, email, phone, location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all shadow-sm"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white appearance-none shadow-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white appearance-none shadow-sm"
              >
                <option value="name">Sort by Name</option>
                <option value="email">Sort by Email</option>
                <option value="location">Sort by Location</option>
                <option value="status">Sort by Status</option>
              </select>

              {/* Sort Order */}
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="flex items-center justify-center px-3 sm:px-4 py-3 text-sm bg-gray-100 hover:bg-gray-200 active:bg-gray-300 border border-gray-300 rounded-lg transition-colors touch-manipulation shadow-sm"
              >
                {sortOrder === 'asc' ? '↓' : '↑'}{' '}
                {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </button>

              {/* Items Per Page */}
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white appearance-none shadow-sm"
              >
                <option value="5">5 per page</option>
                <option value="10">10 per page</option>
                <option value="20">20 per page</option>
                <option value="50">50 per page</option>
              </select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || statusFilter !== 'all') && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                }}
                className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Providers Display */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                Provider List ({filteredProviders.length}{' '}
                {filteredProviders.length === 1 ? 'provider' : 'providers'} found)
              </h3>
              <div className="hidden sm:block text-xs sm:text-sm text-gray-500">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredProviders.length)} of{' '}
                {filteredProviders.length} providers
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Provider Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedProviders.map((provider) => (
                  <tr
                    key={provider.slug}
                    className="hover:bg-gradient-to-r hover:from-emerald-50 hover:to-transparent transition-all duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                          {provider.company_logo ? (
                            <img
                              src={provider.company_logo}
                              alt={provider.company_name}
                              className="w-12 h-12 rounded-xl object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold text-lg">
                              {provider.company_name?.charAt(0) || 'P'}
                            </span>
                          )}
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-2">
                            {provider.company_name || 'N/A'}
                            {getVerificationBadge(provider)}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {provider.contact_person || 'No contact person'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {provider.email ? (
                          <div className="text-sm text-gray-900 flex items-center truncate">
                            <Mail className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{provider.email}</span>
                          </div>
                        ) : null}
                        <div className="text-sm text-gray-500 flex items-center truncate">
                          <Phone className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{provider.phone_number || 'N/A'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center min-w-0">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="min-w-0">
                          {provider.location ? (
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {provider.location}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                          {provider.region ? (
                            <div className="text-sm text-gray-500 truncate">{provider.region}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      {getStatusBadge(provider)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewPolicies(provider)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors hover:bg-blue-50 rounded-lg touch-manipulation"
                          title="View Policies"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleStatusChange(
                              provider,
                              provider.status === 1 ? 'deactivate' : 'activate'
                            )
                          }
                          className={`p-2 transition-colors hover:bg-gray-50 rounded-lg touch-manipulation ${
                            provider.status === 1
                              ? 'text-red-500 hover:text-red-600'
                              : 'text-green-500 hover:text-green-600'
                          }`}
                          title={
                            provider.status === 1
                              ? 'Deactivate Provider'
                              : 'Activate Provider'
                          }
                        >
                          {provider.status === 1 ? (
                            <PowerOff className="w-4 h-4" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden divide-y divide-gray-200">
            {paginatedProviders.map((provider) => (
              <div
                key={provider.slug}
                className="p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md mr-3">
                      {provider.company_logo ? (
                        <img
                          src={provider.company_logo}
                          alt={provider.company_name}
                          className="w-12 h-12 rounded-xl object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-lg">
                          {provider.company_name?.charAt(0) || 'P'}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-semibold text-gray-900 truncate flex items-center gap-2">
                        {provider.company_name || 'N/A'}
                        {getVerificationBadge(provider)}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {provider.contact_person || 'No contact person'}
                      </div>
                      <div className="mt-1">{getStatusBadge(provider)}</div>
                    </div>
                  </div>
                  <div className="flex space-x-1 ml-2">
                    <button
                      onClick={() => handleViewPolicies(provider)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors hover:bg-blue-50 rounded-lg touch-manipulation"
                      title="View Policies"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        handleStatusChange(
                          provider,
                          provider.status === 1 ? 'deactivate' : 'activate'
                        )
                      }
                      className={`p-2 transition-colors hover:bg-gray-50 rounded-lg touch-manipulation ${
                        provider.status === 1
                          ? 'text-red-500 hover:text-red-600'
                          : 'text-green-500 hover:text-green-600'
                      }`}
                      title={
                        provider.status === 1
                          ? 'Deactivate Provider'
                          : 'Activate Provider'
                      }
                    >
                      {provider.status === 1 ? (
                        <PowerOff className="w-4 h-4" />
                      ) : (
                        <Power className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {/* Contact Info */}
                  <div className="space-y-2">
                    {provider.email && (
                      <div className="flex items-center text-sm">
                        <Mail className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-gray-900 truncate">{provider.email}</span>
                      </div>
                    )}
                    <div className="flex items-center text-sm">
                      <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-gray-900 truncate">
                        {provider.phone_number || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      {provider.location ? (
                        <span className="text-gray-900 truncate">{provider.location}</span>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </div>
                    {provider.region && (
                      <div className="text-sm text-gray-500 truncate ml-6">
                        {provider.region}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredProviders.length === 0 && !loading && (
            <div className="text-center py-12 lg:py-16">
              <Building2 className="mx-auto h-12 w-12 lg:h-16 lg:w-16 text-gray-300" />
              <h3 className="mt-4 text-base lg:text-lg font-medium text-gray-900">
                No providers found
              </h3>
              <p className="mt-2 text-sm lg:text-sm text-gray-500 max-w-sm mx-auto px-4">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria to find providers.'
                  : 'No providers have been registered yet.'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredProviders.length > 0 && (
          <div className="bg-white rounded-xl p-3 sm:p-4 shadow-lg border border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
              <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredProviders.length)} of{' '}
                {filteredProviders.length} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors touch-manipulation flex items-center"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-500">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 text-xs sm:text-sm rounded-lg transition-colors touch-manipulation ${
                            currentPage === page
                              ? 'bg-emerald-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    ))}
                </div>
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors touch-manipulation flex items-center"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
              onClick={actionLoading ? undefined : closeModal}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in-0 zoom-in-95 duration-200">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`p-3 rounded-full ${
                    modalAction === 'deactivate' ? 'bg-orange-100' : 'bg-green-100'
                  }`}
                >
                  {modalAction === 'deactivate' ? (
                    <PowerOff className="w-6 h-6 text-orange-600" />
                  ) : (
                    <Power className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <button
                  onClick={closeModal}
                  disabled={actionLoading}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {modalAction === 'activate'
                    ? 'Activate Provider'
                    : 'Deactivate Provider'}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {modalAction === 'activate' ? (
                    <>
                      Are you sure you want to{' '}
                      <span className="font-semibold text-green-600">activate</span> the account
                      for <span className="font-semibold">{modalData.company_name}</span>?
                      <br />
                      <br />
                      This will enable the provider to access their account and perform authorized
                      actions.
                    </>
                  ) : (
                    <>
                      Are you sure you want to{' '}
                      <span className="font-semibold text-orange-600">deactivate</span> the account
                      for <span className="font-semibold">{modalData.company_name}</span>?
                      <br />
                      <br />
                      This will prevent the provider from accessing their account until
                      reactivated.
                    </>
                  )}
                </p>
              </div>

              {/* Provider Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
                    {modalData.company_logo ? (
                      <img
                        src={modalData.company_logo}
                        alt={modalData.company_name}
                        className="w-10 h-10 rounded-xl object-cover"
                      />
                    ) : (
                      <span className="text-white font-medium text-sm">
                        {modalData.company_name?.charAt(0) || 'P'}
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {modalData.company_name}
                    </div>
                    <div className="text-sm text-gray-500">{modalData.email}</div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={closeModal}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={executeAction}
                  disabled={actionLoading}
                  className={`flex-1 px-4 py-3 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    modalAction === 'activate'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-orange-600 hover:bg-orange-700'
                  }`}
                >
                  {actionLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {modalAction === 'activate' ? 'Activating...' : 'Deactivating...'}
                    </div>
                  ) : modalAction === 'activate' ? (
                    'Activate Provider'
                  ) : (
                    'Deactivate Provider'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default ProviderManagement
